import { http, HttpResponse } from 'msw';

import type { HubPayrollSetupProgressData } from 'services/hub/queries/useHubPayrollSetupProgress/useHubPayrollSetupProgress.interface';
import type { ReasonableCompensationRecord } from 'services/hub/queries/useReasonableCompensation/useReasonableCompensation.interface';
import type {
    SearchOperatorTasksResponse,
    OperatorTaskInstance,
    OperatorTaskTemplate,
    OperatorTaskMember,
} from 'services/hub/queries/useOperatorTasks/interfaces';

// ────────────────────────────────────────────────────────────────────
// Mock data factories
// ────────────────────────────────────────────────────────────────────

const makeTaskTemplate = (name: string, displayName: string, instructions: string): OperatorTaskTemplate => ({
    id: 1,
    name,
    display_name: displayName,
    instructions,
    sla_hours: 48,
    tools: [],
    resources: {},
    is_member_task: false,
});

const mockMember: OperatorTaskMember = {
    id: 1,
    uuid: 'member-uuid-1',
    fullname: 'Jane Smith',
    email: 'jane@example.com',
    full_business_name: 'Smith Consulting LLC',
    home_address_state: 'TX',
    business_address_state: 'TX',
    established_date: '2024-01-01',
    usertype: 'LLC-SC',
    business: {
        id: 1,
        state_of_inc: { id: 'CA', name: 'California' },
        s_corp_election_date: '2025-06-01',
        naics_business_activity: '541511',
    },
};

const makeTaskInstance = (
    id: string,
    templateName: string,
    displayName: string,
    instructions: string,
    overrides: Partial<OperatorTaskInstance> = {}
): OperatorTaskInstance => ({
    id,
    status: 'ready',
    priority: 2,
    context: null,
    source: 'system',
    task_labels: ['payroll_setup', 'onboarding'],
    due_date: '2026-04-10',
    flagged: false,
    is_in_sla: true,
    effective_sla_hours: 48,
    created_at: '2026-04-01T10:00:00Z',
    updated_at: '2026-04-01T10:00:00Z',
    ready_at: '2026-04-01T10:00:00Z',
    task_type: templateName,
    task_template: makeTaskTemplate(templateName, displayName, instructions),
    member: mockMember,
    operator: { id: 2001, fullname: 'Test Employee' },
    waiting_on: null,
    cancellation_reason: null,
    expires_at: null,
    ...overrides,
});

const makeSearchResponse = (tasks: OperatorTaskInstance[]): SearchOperatorTasksResponse => ({
    count: tasks.length,
    limit: 25,
    offset: 0,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- OperatorTaskInstance is compatible for story mocks
    results: tasks as any,
});

// ────────────────────────────────────────────────────────────────────
// Shared mock data — exported for story overrides
// ────────────────────────────────────────────────────────────────────

export const mockPayrollSetupProgress: HubPayrollSetupProgressData = {
    current_step: 'reasonable_compensation',
    step_statuses: {
        payroll_migration: 'complete',
        intro_question: 'complete',
        review_company_info: 'complete',
        company_bank_account: 'complete',
        pay_schedule: 'complete',
        review_personal_info: 'complete',
        work_address: 'complete',
        reasonable_compensation_education: 'complete',
        financial_business_details: 'complete',
        reasonable_compensation: 'incomplete',
        employee_bank_account: 'incomplete',
        federal_tax: 'incomplete',
        state_tax: 'incomplete',
        deductions: 'incomplete',
        sign_documents: 'incomplete',
        team_members: 'incomplete',
        add_employees: 'incomplete',
        add_contractors: 'incomplete',
    },
    is_completed: false,
    payroll_scope: 'JUST_ME',
    has_retirement_interest: true,
    has_healthcare_interest: false,
    reasonable_compensation_amount: 75000,
    w2_employee_count: 2,
    physical_location: { city: 'Austin', state: 'TX' },
    has_previous_payroll: false,
    previous_payroll_provider: null,
    previous_pay_schedule: null,
    anticipated_pay_date: null,
    employee_count: null,
    terminated_employee_count: null,
    has_remote_employees: false,
    has_employees: false,
};

const mockReasonableCompensation: ReasonableCompensationRecord = {
    uuid: 'rc-uuid-1',
    benchmark_year: 2024,
    benchmark_low_value: '50000.00',
    benchmark_high_value: '100000.00',
    benchmark_suggested_value: '75000.00',
    annual_salary: '80000.00',
    is_valid: true,
    created_at: '2024-01-01T00:00:00Z',
};

export const mockBenefitsTask = makeTaskInstance(
    'task-benefits-1',
    'benefits_on_payroll',
    'Benefits on Payroll',
    'Reach out to the user to get more information on the deductions they want to add to payroll'
);

const mockPayrollMigrationTask = makeTaskInstance(
    'task-migration-1',
    'payroll_migration',
    'Payroll Migration',
    'Migrate the member from their previous payroll provider into Gusto.'
);

// ────────────────────────────────────────────────────────────────────
// Default handlers — merged into global defaults via defaults.ts
// ────────────────────────────────────────────────────────────────────

export default {
    hubPayrollSetupProgress: http.get('*/hub/members/:memberId/payroll-setup-progress/', () =>
        HttpResponse.json(mockPayrollSetupProgress)
    ),
    hubReasonableCompensation: http.get('*/hub/members/:memberId/reasonable-compensation/', () =>
        HttpResponse.json(mockReasonableCompensation)
    ),
    hubTasksSearch: http.post('*/v1/hub/tasks/search/', () =>
        HttpResponse.json(makeSearchResponse([mockBenefitsTask, mockPayrollMigrationTask]))
    ),
    hubTasksStats: http.post('*/hub/tasks/stats/', () =>
        HttpResponse.json({
            total: 2,
            stats: [
                { task_template_name: 'benefits_on_payroll', count: 1, status: 'ready' },
                { task_template_name: 'payroll_migration', count: 1, status: 'ready' },
            ],
        })
    ),
    hubTaskById: http.get('*/v1/hub/tasks/:taskId/', () => HttpResponse.json(mockBenefitsTask)),
    hubTaskComplete: http.post('*/v1/hub/tasks/:taskId/status/', () => HttpResponse.json({ status: 'completed' })),
    hubTaskComments: http.get('*/v1/hub/tasks/:taskId/comments/', () => HttpResponse.json([])),
    hubUser: http.get('*/hub/user/', () =>
        HttpResponse.json({ pk: 2001, email: 'employee@collective.com', first_name: 'Test', last_name: 'Employee' })
    ),
    hubMember: http.get('*/hub/members/:memberId/', () =>
        HttpResponse.json({
            id: 1,
            fullname: 'Jane Smith',
            email: 'jane@example.com',
            businessphonenumber: '(512) 555-1234',
            dob: '1990-01-15',
            EIN: '12-3456789',
            stateofoperation: 'TX',
            establisheddate: '2024-01-01',
            member_relationship_manager: 'John MRM',
            business: {
                id: 1,
                state_of_inc: { id: 'CA', name: 'California' },
                s_corp_election_date: '2025-06-01',
                naics_business_activity: '541511',
            },
        })
    ),
};
